import { useState } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Tag,
  Popconfirm,
  message,
  Empty,
  Spin,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { UserPlus } from "lucide-react";
import { IAdminUser, TAdminRole } from "../../../types/api/adminUser";
import {
  useAdminUsers,
  useInviteAdmin,
  useUpdateAdmin,
  useDeactivateAdmin,
} from "../../../hooks/api/users/hooks";

const ROLE_OPTS = [
  { value: "admin", label: "Admin" },
  { value: "manager", label: "Manager" },
];

const INVITE_ROLE_OPTS = [
  { value: "admin", label: "Admin" },
  { value: "manager", label: "Manager" },
];

const UsersPage = () => {
  const [inviteOpen, setInviteOpen] = useState(false);
  const [form] = Form.useForm();

  const { data, isLoading } = useAdminUsers();
  const users: IAdminUser[] = (data?.data as IAdminUser[] | undefined) ?? [];

  const { mutate: inviteAdmin, isPending: inviting } = useInviteAdmin({
    onSuccess: () => {
      message.success("Invitation sent successfully.");
      setInviteOpen(false);
      form.resetFields();
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      message.error(err?.response?.data?.message ?? err?.message ?? "Failed to send invitation.");
    },
  });

  const { mutate: updateAdmin } = useUpdateAdmin({
    onSuccess: () => {
      message.success("Role updated.");
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      message.error(err?.response?.data?.message ?? err?.message ?? "Failed to update role.");
    },
  });

  const { mutate: deactivateAdmin } = useDeactivateAdmin({
    onSuccess: () => {
      message.success("User status updated.");
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      message.error(err?.response?.data?.message ?? err?.message ?? "Failed to update user.");
    },
  });

  const columns: ColumnsType<IAdminUser> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (name: string) => (
        <span className="font-medium text-slate-800">{name || "—"}</span>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (email: string) => (
        <span className="text-slate-600 text-sm">{email}</span>
      ),
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      render: (role: TAdminRole, record) => (
        <Select
          size="small"
          value={role}
          style={{ width: 120 }}
          options={ROLE_OPTS}
          onChange={(val) => updateAdmin({ id: record.id, role: val })}
        />
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) =>
        status === "active" ? (
          <Tag color="success">Active</Tag>
        ) : (
          <Tag color="error">Inactive</Tag>
        ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 140,
      render: (_, record) =>
        record.status === "active" ? (
          <Popconfirm
            title="Deactivate this user?"
            description="They will lose access to the dashboard."
            onConfirm={() => deactivateAdmin(record.id)}
            okText="Deactivate"
            okButtonProps={{ danger: true }}
          >
            <Button danger size="small">
              Deactivate
            </Button>
          </Popconfirm>
        ) : (
          <Popconfirm
            title="Activate this user?"
            onConfirm={() => updateAdmin({ id: record.id, status: "active" })}
            okText="Activate"
          >
            <Button size="small">Activate</Button>
          </Popconfirm>
        ),
    },
  ];

  function onFinish(values: { name: string; email: string; role: TAdminRole }) {
    inviteAdmin(values);
  }

  return (
    <div className="p-6 min-h-screen bg-slate-50/50">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-slate-800">Team Members</h1>
          <p className="text-slate-400 text-sm">Manage dashboard access</p>
        </div>
        <Button
          type="primary"
          icon={<UserPlus size={15} />}
          className="flex items-center gap-1.5"
          onClick={() => setInviteOpen(true)}
        >
          Invite Member
        </Button>
      </div>

      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center py-20">
            <Spin size="large" />
          </div>
        ) : users.length === 0 ? (
          <Empty
            className="py-20"
            description="Invite your first team member"
          />
        ) : (
          <Table<IAdminUser>
            dataSource={users.map((u) => ({ ...u, key: u.id }))}
            columns={columns}
            pagination={false}
            rowKey="id"
          />
        )}
      </div>

      <Modal
        open={inviteOpen}
        onCancel={() => {
          setInviteOpen(false);
          form.resetFields();
        }}
        title={
          <span className="text-base font-semibold">Invite Team Member</span>
        }
        width="min(480px, 95vw)"
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setInviteOpen(false);
              form.resetFields();
            }}
          >
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={inviting}
            onClick={() => form.submit()}
          >
            Send Invitation
          </Button>,
        ]}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          size="middle"
          className="pt-2"
          onFinish={onFinish}
        >
          <Form.Item
            name="name"
            label="Full Name"
            rules={[{ required: true, message: "Name is required" }]}
          >
            <Input placeholder="e.g. Jane Smith" />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: "Email is required" },
              { type: "email", message: "Enter a valid email" },
            ]}
          >
            <Input placeholder="jane@company.com" />
          </Form.Item>
          <Form.Item
            name="role"
            label="Role"
            initialValue="manager"
            rules={[{ required: true, message: "Role is required" }]}
          >
            <Select options={INVITE_ROLE_OPTS} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UsersPage;
